import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  chakra,
  Container,
  Flex,
  Grid,
  Heading,
  HStack,
  Image,
  Text,
  VStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AlertCircle, CheckCircle, XCircle, Lightbulb, BookOpen } from 'lucide-react';
import Layout from "../../../components/student/Layout/layout";
import { getAllChaptersRequest } from "../../../modules/students-modules/chapters/Actions";
import { getMissedQuestionsRequest } from "../../../modules/students-modules/questions/Actions";
import {
  createStudentExamsRequest
} from "../../../modules/students-modules/student-exams/Actions";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const Questions = () => {
  const baseUrl = "https://asquera.com/asquera";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const bookmark = useSelector((state) => state.questions.bookmark);
  const missed = useSelector((state) => state.questions.missed);
  const chapters = useSelector(
    (state) => state.studentChapters.allChapters
  );

  const [selectedTab, setSelectedTab] = useState(1);
  useEffect(() => {
    // dispatch(getBookmarkedQuestionsRequest());
    dispatch(getAllChaptersRequest());
  }, []);

  useEffect(() => {
    dispatch(getMissedQuestionsRequest({ type: selectedTab }));
  }, [selectedTab]);

  const renderQuestion = (question, index) => {
    const { question: questionData, answers, student_answer_id } = question;
    
    return (
      <div key={`${questionData.id || 'noid'}-${index}`} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-start space-x-4 mb-4">
          <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-orange-600 font-semibold text-sm">{index + 1}</span>
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                {questionData.comprehension && (
                  <div className="mb-3">
                    <span className="text-sm font-medium text-gray-500">Comprehension:</span>
                    <div className="text-gray-700 mt-1" dangerouslySetInnerHTML={{ __html: questionData.comprehension }} />
                  </div>
                )}
                <div
                  className="text-gray-900 font-medium"
                  dangerouslySetInnerHTML={{ __html: questionData.question }}
                />
              </div>
            </div>

            {questionData.image && (
              <div className="mb-4">
                <img
                  src={`${baseUrl}/${questionData.image}`}
                  alt="Question"
                  className="max-w-full h-auto max-h-48 rounded-lg border border-gray-200"
                />
              </div>
            )}

            <div className="space-y-3">
              {questionData.answers.map((answer, answerIndex) => {
                const isCorrect = answer?.right_answer === 1;
                const isStudentAnswer = student_answer_id && answer?.id && student_answer_id === answer?.id;
                const isWrongStudentAnswer = isStudentAnswer && !isCorrect;

                return (
                  <div
                    key={answer.id}
                    className={`flex items-center space-x-3 p-3 rounded-lg border-2 transition-all ${isCorrect
                      ? 'border-green-200 bg-green-50'
                      : isWrongStudentAnswer
                        ? 'border-red-200 bg-red-50'
                        : 'border-gray-200 bg-gray-50'
                      }`}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="w-6 h-6 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center text-sm font-medium">
                        {String.fromCharCode(97 + answerIndex)}
                      </span>
                      {isCorrect && (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      )}
                      {isWrongStudentAnswer && (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    <span className={`flex-1 ${isCorrect ? 'text-green-800 font-medium' :
                      isWrongStudentAnswer ? 'text-red-800' : 'text-gray-700'
                      }`}>
                      {answer?.answer}
                    </span>
                    {isStudentAnswer && (
                      <span className={`text-xs px-2 py-1 rounded-full ${isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                        Your answer
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout color="main_1" bg="white" isLoading={false}>
      <Container maxW="container.xl" h="100%" my="100px" mb="165px" w="100%">
        <VStack spacing={8} h="100%" w="100%">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden w-full">
            <div className="border-b border-gray-200">
              <div className="flex items-center space-x-3 p-6 pb-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Most Missed Questions</h3>
              </div>
            </div>

            <div className="p-6">
              {/* Create Exam Button */}
              <div className="mb-6">
                <Menu>
                  <MenuButton as={Button} bg="main_1" color="white" _hover={{ bg: "main_1" }} className="flex items-center space-x-2">
                    <span>Create Exam From Missed Questions</span>
                  </MenuButton>

                  <MenuList bg="white" borderRadius="12px" p={4} w="100%" boxShadow="0 10px 25px rgba(0,0,0,0.1)">
                    <MenuItem 
                      bg="white" 
                      borderRadius="8px" 
                      _hover={{ bg: "gray.50" }}
                      onClick={() => {
                        dispatch(createStudentExamsRequest({
                          type: 8,
                          sub_type: 8,
                          chapters: [],
                          question_number: 10,
                          name: "Missed Questions Exam" + new Date().toLocaleDateString('en-GB'),
                        }, () => {
                          toast.success("Exam created successfully");
                          navigate(`/students/exams`);
                        }));
                      }}
                    >
                      <Flex alignItems="center" gap={2}>
                        <Text fontWeight="medium">10 Questions</Text>
                      </Flex>
                    </MenuItem>
                    <MenuItem 
                      bg="white" 
                      borderRadius="8px" 
                      _hover={{ bg: "gray.50" }}
                      disabled={missed.length >= 30}
                      onClick={() => {
                        dispatch(createStudentExamsRequest({
                          type: 8,
                          sub_type: 8,
                          chapters: [],
                          question_number: 30,
                          name: "Missed Questions Exam " + new Date().toLocaleDateString('en-GB'),
                        }, () => {
                          toast.success("Exam created successfully");
                          navigate(`/students/exams`);
                        }));
                      }}
                    >
                      <Flex alignItems="center" gap={2}>
                        <Text fontWeight="medium">30 Questions</Text>
                      </Flex>
                    </MenuItem>
                    <MenuItem 
                      bg="white" 
                      borderRadius="8px" 
                      _hover={{ bg: "gray.50" }}
                      disabled={missed.length >= 50}
                      onClick={() => {
                        dispatch(createStudentExamsRequest({
                          type: 8,
                          sub_type: 8,
                          chapters: [],
                          question_number: 50,
                          name: "Missed Questions Exam " + new Date().toLocaleDateString('en-GB'),
                        }, () => {
                          toast.success("Exam created successfully");
                          navigate(`/students/exams`);
                        }));
                      }}
                    >
                      <Flex alignItems="center" gap={2}>
                        <Text fontWeight="medium">50 Questions</Text>
                      </Flex>
                    </MenuItem>
                  </MenuList>
                </Menu>
              </div>

              {/* Tab Buttons */}
              <div className="flex space-x-1 mb-6">
                <button
                  onClick={() => setSelectedTab(1)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${selectedTab === 1
                    ? 'bg-orange-50 text-orange-600 border-2 border-orange-600'
                    : 'text-gray-600 hover:text-gray-900 border-2 border-transparent'
                    }`}
                >
                  Normal Exams
                </button>
                <button
                  onClick={() => setSelectedTab(7)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${selectedTab === 7
                    ? 'bg-orange-50 text-orange-600 border-2 border-orange-600'
                    : 'text-gray-600 hover:text-gray-900 border-2 border-transparent'
                    }`}
                >
                  Study with Enough Exams
                </button>
              </div>

              {/* Questions by Chapter */}
              <div className="space-y-6">
                {missed?.length !== 0 && chapters?.map((chapter, chapterIndex) => {
                  const chapterQuestions = missed.filter(question => 
                    question?.question?.chapter_id === chapter?.id
                  );
                  
                  if (chapterQuestions.length === 0) return null;

                  return (
                    <div key={chapter.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="bg-gradient-to-r from-yellow-400 to-orange-500 px-6 py-4">
                        <h4 className="text-lg font-semibold text-white">{chapter?.name}</h4>
                        <p className="text-yellow-100 text-sm mt-1">{chapterQuestions.length} questions</p>
                      </div>
                      <div className="p-6">
                        <div className="space-y-4">
                          {chapterQuestions.map((question, index) => renderQuestion(question, index))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </VStack>
      </Container>
    </Layout>
  );
};

export default Questions;
